import React from 'react'
import NextLink from 'next/link'

import { Formik, FormikConfig } from 'formik'

import routesConfig from '@mindfulchefuk/config/routesConfig'
import MainLayout from '@mindfulchefuk/layouts/MainLayout'
import { requestPasswordReset } from '@mindfulchefuk/services/passwordReset'

import {
  TertiaryButton,
  Box,
  Text,
  IllustrationContainer,
} from '@mindfulchefuk/design-system'
import { NextPageWithLayout } from '@mindfulchefuk/types/NextJS'
import { ForgotPasswordForm } from '@mindfulchefuk/features/UsersForms/ForgotPasswordForm'
import { addNotification } from '@mindfulchefuk/features/Notifications/notificationsSlice'
import { useDispatch } from 'react-redux'

type ForgotPasswordData = {
  email: string
}

const ForgotPassword: NextPageWithLayout = () => {
  const dispatch = useDispatch()

  const submitHandler: FormikConfig<ForgotPasswordData>['onSubmit'] = async ({
    email,
  }) => {
    await requestPasswordReset(email)
    dispatch(addNotification({ id: 'success-reset-link-sent' }))
  }

  return (
    <Box overflow="hidden">
      <IllustrationContainer
        color="aubergine"
        py={{ base: 24, sm: 36 }}
        illustrations="10"
      >
        <Box mx="auto" maxWidth="440px">
          <Text
            as="h1"
            variant="heading-lg"
            textAlign="center"
            mx="auto"
            maxWidth={{ base: 'calc(100% - 100px)', sm: '440px' }}
          >
            Forgot your password?
          </Text>
          <Text
            as="p"
            variant="body-lg"
            mt={{ base: 12, sm: 16 }}
            textAlign="center"
          >
            Please enter the email address associated with your account, and
            we&apos;ll email you a link to reset your password.
          </Text>
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={{
              email: '',
            }}
            onSubmit={submitHandler}
          >
            <ForgotPasswordForm />
          </Formik>
          <Box textAlign="center">
            <NextLink href={routesConfig.signIn.pathname} passHref>
              <TertiaryButton variant="primary" size="medium" as="a">
                Back to log in
              </TertiaryButton>
            </NextLink>
          </Box>
        </Box>
      </IllustrationContainer>
    </Box>
  )
}

ForgotPassword.getLayout = (page) => (
  <MainLayout
    title={routesConfig.forgotPassword.label}
    description="Forgotten your password? Enter your email address and we'll send you a link to reset it."
  >
    {page}
  </MainLayout>
)

export default ForgotPassword

export { getStaticGlobalContent as getStaticProps } from '@mindfulchefuk/features/CMS/services/getStaticData'
